import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Clock, FlaskConical, Loader2, Send, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useRoute, useSearch } from "wouter";
import { PageHeader } from "@/components/page-header";
import {
  type AnswerData,
  type QuestionData,
  SurveyResponseViewer,
} from "@/components/surveys/survey-response-viewer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { formatDate, formatDateTime } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

export default function MySurveyResponse() {
  const [, params] = useRoute("/my-surveys/:instanceId");
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const isTestMode = useMemo(
    () => new URLSearchParams(searchString).get("test") === "true",
    [searchString],
  );
  const instanceId = params?.instanceId;
  const [answers, setAnswers] = useState<
    Record<string, { answerText?: string; answerValue?: number }>
  >({});
  const { toast } = useToast();

  const {
    data: surveyDetail,
    isLoading,
    error,
  } = useQuery({
    ...(isTestMode
      ? orpc.pulseSurveys.testResponse.queryOptions({ input: { id: instanceId! } })
      : orpc.pulseSurveys.mySurveyInstance.queryOptions({ input: { instanceId: instanceId! } })),
    enabled: !!instanceId,
  });

  const submitResponse = useMutation({
    mutationFn: async () => {
      const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
        questionId,
        answerText: answer.answerText,
        answerValue: answer.answerValue,
      }));
      return rpcClient.pulseSurveys.submitResponse({
        instanceId: instanceId!,
        answers: answersArray,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.mySurveys.queryOptions().queryKey,
      });
      toast({ title: "Resposta enviada com sucesso!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao enviar resposta",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmitResponse = () => {
    if (!surveyDetail || isTestMode) return;

    const requiredQuestions = surveyDetail.questions?.filter((q) => q.required);
    const missingAnswers = requiredQuestions?.filter((q) => {
      const answer = answers[q.id];
      if (!answer) return true;
      if (
        (q.questionType === "short_text" ||
          q.questionType === "text" ||
          q.questionType === "long_text") &&
        !answer.answerText?.trim()
      )
        return true;
      if (q.questionType === "scale" && answer.answerValue === undefined) return true;
      if (q.questionType === "single_choice" && !answer.answerText?.trim()) return true;
      return false;
    });

    if (missingAnswers && missingAnswers.length > 0) {
      toast({
        title: "Preencha todas as perguntas obrigatórias",
        variant: "destructive",
      });
      return;
    }

    submitResponse.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const goBack = () => {
    if (isTestMode && surveyDetail?.survey?.id) {
      navigate(`/surveys/${surveyDetail.survey.id}`);
    } else {
      navigate("/my-surveys");
    }
  };

  if (error || !surveyDetail) {
    return (
      <div className="flex-1 space-y-6">
        <Button variant="ghost" onClick={goBack} data-testid="button-back-surveys">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isTestMode ? "Voltar para Pesquisa" : "Voltar para Pesquisas"}
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground text-center">
              Pesquisa não encontrada ou você não tem acesso.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCancelled = !isTestMode && surveyDetail.instance.status === "cancelled";
  const isPastDeadline = !isTestMode && new Date() > new Date(surveyDetail.instance.deadline);

  if (isCancelled) {
    return (
      <div className="flex-1 space-y-6">
        <Button variant="ghost" onClick={goBack} data-testid="button-back-surveys">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Pesquisas
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">Esta pesquisa foi cancelada</p>
            <p className="text-sm text-muted-foreground mt-2">
              Esta instancia da pesquisa nao esta mais disponivel para resposta.
            </p>
            <Button
              variant="outline"
              className="mt-6"
              onClick={goBack}
              data-testid="button-back-to-list"
            >
              Voltar para Pesquisas
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isTestMode && surveyDetail.hasResponded) {
    const viewerQuestions: QuestionData[] = (surveyDetail.questions || []).map((q) => ({
      id: q.id,
      questionText: q.questionText,
      description: q.description,
      questionType: q.questionType,
      required: q.required,
      scaleMin: q.scaleMin,
      scaleMax: q.scaleMax,
      scaleMinLabel: q.scaleMinLabel,
      scaleMaxLabel: q.scaleMaxLabel,
      options: q.options,
    }));

    const viewerAnswers: AnswerData[] = (surveyDetail.submittedAnswers || []).map((a) => ({
      questionId: a.pulseQuestionInstanceId,
      answerText: a.answerText,
      answerValue: a.answerValue,
    }));

    return (
      <div className="flex-1 space-y-6">
        <PageHeader
          title={surveyDetail.survey.title}
          description={surveyDetail.survey.description || undefined}
          onBack={goBack}
        />

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-[hsl(var(--forest-pulse))]">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-semibold">Respondido</span>
          </div>
          {surveyDetail.response?.submittedAt && (
            <span className="text-sm text-[hsl(var(--ink-muted))]">
              em {formatDateTime(surveyDetail.response.submittedAt)}
            </span>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <SurveyResponseViewer questions={viewerQuestions} answers={viewerAnswers} />
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button variant="outline" onClick={goBack} data-testid="button-back-to-list">
            Voltar para Pesquisas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title={surveyDetail.survey.title}
        description={surveyDetail.survey.description || undefined}
        onBack={goBack}
      />

      {isTestMode && (
        <div
          className="flex items-center gap-3 p-3 rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-2))]"
          data-testid="test-mode-banner"
        >
          <FlaskConical className="h-5 w-5 text-[hsl(var(--amber-state))] shrink-0" />
          <div>
            <p className="text-sm font-semibold text-[hsl(var(--ink-primary))]">Modo de teste</p>
            <p className="text-xs text-[hsl(var(--ink-muted))]">
              Visualize a pesquisa como seus colaboradores verão. As respostas não serão salvas.
            </p>
          </div>
        </div>
      )}

      {!isTestMode && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>Prazo: {formatDate(surveyDetail.instance.deadline)}</span>
        </div>
      )}

      {isPastDeadline ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-[hsl(var(--alert-red))] mb-4" />
            <p className="text-lg font-medium">O prazo para responder expirou</p>
            <Button
              variant="outline"
              className="mt-6"
              onClick={goBack}
              data-testid="button-back-to-list"
            >
              Voltar para Pesquisas
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {surveyDetail.questions?.length > 0 ? (
                surveyDetail.questions.map((question, index) => {
                  const questionOptions = (question.options as Array<{ label: string }>) || [];
                  return (
                    <div key={question.id} className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium">
                          {index + 1}. {question.questionText}
                          {question.required && (
                            <span className="text-[hsl(var(--alert-red))] ml-1">*</span>
                          )}
                        </Label>
                        {question.description && (
                          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                            {question.description}
                          </p>
                        )}
                      </div>

                      {question.questionType === "short_text" ||
                      question.questionType === "text" ? (
                        <Input
                          placeholder="Digite sua resposta..."
                          value={answers[question.id]?.answerText ?? ""}
                          onChange={(e) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [question.id]: { answerText: e.target.value },
                            }))
                          }
                          data-testid={`input-answer-${question.id}`}
                        />
                      ) : question.questionType === "long_text" ? (
                        <Textarea
                          placeholder="Digite sua resposta..."
                          value={answers[question.id]?.answerText ?? ""}
                          onChange={(e) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [question.id]: { answerText: e.target.value },
                            }))
                          }
                          className="min-h-[100px]"
                          data-testid={`input-answer-${question.id}`}
                        />
                      ) : question.questionType === "single_choice" &&
                        questionOptions.length > 0 ? (
                        <RadioGroup
                          value={answers[question.id]?.answerText ?? ""}
                          onValueChange={(value) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [question.id]: { answerText: value },
                            }))
                          }
                          className="space-y-2"
                          data-testid={`input-answer-${question.id}`}
                        >
                          {questionOptions.map((opt, optIdx) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: survey options may have duplicate labels from user input
                            <div key={optIdx} className="flex items-center space-x-2">
                              <RadioGroupItem
                                value={opt.label}
                                id={`${question.id}-opt-${optIdx}`}
                                data-testid={`radio-option-${question.id}-${optIdx}`}
                              />
                              <Label
                                htmlFor={`${question.id}-opt-${optIdx}`}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {opt.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      ) : question.questionType === "scale" ? (
                        <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                          <div className="px-2 relative">
                            <Slider
                              min={question.scaleMin || 1}
                              max={question.scaleMax || 5}
                              step={1}
                              value={[answers[question.id]?.answerValue || question.scaleMin || 1]}
                              onValueChange={(value) =>
                                setAnswers((prev) => ({
                                  ...prev,
                                  [question.id]: { answerValue: value[0] },
                                }))
                              }
                              className="cursor-pointer"
                            />
                            <div className="flex justify-between px-1 mt-2">
                              {Array.from(
                                { length: (question.scaleMax || 5) - (question.scaleMin || 1) + 1 },
                                (_, i) => (
                                  // biome-ignore lint/suspicious/noArrayIndexKey: scale tick marks are static and never reordered
                                  <div key={i} className="flex flex-col items-center">
                                    <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                                    <span className="text-xs text-muted-foreground/60 mt-1">
                                      {(question.scaleMin || 1) + i}
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm font-medium px-2">
                            <span className="text-muted-foreground">
                              {question.scaleMinLabel || question.scaleMin}
                            </span>
                            <span className="text-muted-foreground">
                              {question.scaleMaxLabel || question.scaleMax}
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nenhuma pergunta encontrada
                </p>
              )}
            </div>

            {surveyDetail.questions?.length > 0 && (
              <div className="flex justify-end gap-2 pt-6 mt-6 border-t">
                {isTestMode ? (
                  <Button variant="outline" onClick={goBack} data-testid="button-finish-test">
                    <FlaskConical className="h-4 w-4 mr-2" />
                    Concluir Teste
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={goBack} data-testid="button-cancel-response">
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSubmitResponse}
                      disabled={submitResponse.isPending || submitResponse.isSuccess}
                      data-testid="button-submit-response"
                    >
                      {submitResponse.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Enviar Resposta
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
